/**
 * Kleine hulpfuncties in plaats van lodash, dat het origineel gebruikte (`shuffle`, `minBy`,
 * `maxBy`, `round`, `floor`, `min`, `max`). Ze gedragen zich zoals de lodash-versies voor de
 * manier waarop het spel ze aanroept. Toeval komt altijd van buiten (`ActieContext.random`),
 * zodat acties testbaar blijven.
 */
/**
 * Diepe kopie van gewone data (objecten, arrays, primitieven). Mongo gaf bij elke `find` een
 * nieuw object en kopieerde bij elke update; zo raakt niemand per ongeluk de statische data
 * of een ander deel van de staat. `undefined` blijft staan, anders dan bij JSON.
 */
export function kloon(waarde) {
    if (Array.isArray(waarde))
        return waarde.map((element) => kloon(element));
    if (waarde !== null && typeof waarde === 'object') {
        const kopie = {};
        for (const [sleutel, veld] of Object.entries(waarde))
            kopie[sleutel] = kloon(veld);
        return kopie;
    }
    return waarde;
}
/** Een willekeurig element; gooit bij een lege lijst, want dan klopt de aanroep niet. */
export function willekeurigElement(lijst, random) {
    const element = lijst[Math.floor(random() * lijst.length)];
    if (element === undefined)
        throw new Error('Geen element om uit te kiezen');
    return element;
}
/**
 * `_.shuffle` van lodash: een nieuwe, geschudde kopie. Zelfde algoritme (van voor naar
 * achter), zodat een vaste reeks toevalsgetallen dezelfde volgorde geeft als lodash.
 */
export function shuffle(lijst, random) {
    const kopie = [...lijst];
    const laatste = kopie.length - 1;
    for (let index = 0; index < kopie.length; index += 1) {
        const ander = index + Math.floor(random() * (laatste - index + 1));
        const waarde = kopie[ander];
        kopie[ander] = kopie[index];
        kopie[index] = waarde;
    }
    return kopie;
}
/**
 * `shuffleArray` uit naslag/server/server/generateData/player.ts: schudt ter plekke (van
 * achter naar voor) en geeft dezelfde array terug.
 */
export function shuffleArray(array, random) {
    for (let i = array.length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
}
/** `_.minBy`: het eerste element met de kleinste waarde, of `undefined` bij een lege lijst. */
export function minBy(lijst, waarde) {
    let beste;
    let besteWaarde = Infinity;
    for (const element of lijst) {
        const w = waarde(element);
        if (beste === undefined || w < besteWaarde) {
            beste = element;
            besteWaarde = w;
        }
    }
    return beste;
}
/** `_.maxBy`: het eerste element met de grootste waarde, of `undefined` bij een lege lijst. */
export function maxBy(lijst, waarde) {
    let beste;
    let besteWaarde = -Infinity;
    for (const element of lijst) {
        const w = waarde(element);
        if (beste === undefined || w > besteWaarde) {
            beste = element;
            besteWaarde = w;
        }
    }
    return beste;
}
/** `_.min`: het kleinste getal, of `undefined` bij een lege lijst. */
export function min(getallen) {
    return getallen.length === 0 ? undefined : Math.min(...getallen);
}
/** `_.max`: het grootste getal, of `undefined` bij een lege lijst. */
export function max(getallen) {
    return getallen.length === 0 ? undefined : Math.max(...getallen);
}
/**
 * Afronden op `decimalen` zoals lodash dat doet: via de e-notatie in plaats van
 * vermenigvuldigen, zodat bijvoorbeeld `floor(0.7, 1)` 0.7 blijft en geen 0.6 wordt.
 */
function metDecimalen(methode, getal, decimalen) {
    if (decimalen === 0 || !Number.isFinite(getal))
        return methode(getal);
    const [mantisse, exponent = '0'] = `${getal}e`.split('e');
    const verschoven = methode(Number(`${mantisse}e${Number(exponent) + decimalen}`));
    const [mantisse2, exponent2 = '0'] = `${verschoven}e`.split('e');
    return Number(`${mantisse2}e${Number(exponent2) - decimalen}`);
}
/** `_.round`. */
export const round = (getal, decimalen = 0) => metDecimalen(Math.round, getal, decimalen);
/** `_.floor`. */
export const floor = (getal, decimalen = 0) => metDecimalen(Math.floor, getal, decimalen);
